<div class="invite-share" [class.invite-share--dialog]="isDialog" [class.invite-share--compact]="compact">

  <!-- ============================================================
       NEW DESIGN (environment.features.newInviteDesign) — a square invite
       card built on the event's cover in one of four templates, one hero
       share action plus a WhatsApp/Copy/QR row, and the printed QR card.
       With inviteCard.saveUrl set, the card the host is looking at also
       becomes the link preview (see schedulePreviewSave).
       ============================================================ -->
  <ng-container *ngIf="newInviteDesignEnabled; else legacyBody">

    <!-- Eyebrow + title. Only a dialog can close itself; inline, the create
         sheet's own chrome is the way out. On the printed screen the back
         arrow sits up here, like the close button. -->
    <!-- Inline in the create sheet, the sheet's own header already says "Event
         created / Your invite is ready", so this one only shows in a dialog, or
         on the printed screen where it carries the back arrow. -->
    <div class="invite-head invite-head--new" *ngIf="isDialog || screen === 'printed'">
      <div class="invite-head-text" *ngIf="screen === 'invite'; else printedHeadText">
        <div class="created-tag" *ngIf="mode === 'created'">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <path d="M4 12l5 5L20 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span>Event created</span>
        </div>
        <!-- Catalog: invite.share.title.created / invite.share.title.reinvite /
             invite.share.subtitle (Picaggo_Common/content/strings.json). -->
        <h2 class="invite-title">{{ mode === 'created' ? 'Send the invite to start collecting photos' : 'Invite more guests' }}</h2>
        <p class="is-sub">This card is what guests see when you share the link.</p>
        <!-- Right after creating is when a typo gets spotted; hosts without a
             way in made the event again to fix it (2026-10-20). -->
        <button type="button" class="is-edit-details" *ngIf="canEditDetails" (click)="editDetails()">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
            <path d="M4 20h4L18.5 9.5a2.1 2.1 0 00-3-3L5 17v3z" stroke-linejoin="round" />
            <path d="M13.5 8.5l3 3" stroke-linecap="round" />
          </svg>
          Edit details
        </button>
      </div>
      <ng-template #printedHeadText>
        <div class="invite-head-text invite-head-text--printed">
          <button type="button" class="printed-back" (click)="goBackToInvite()" aria-label="Back to sharing">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <path d="M15 6l-6 6 6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div>
            <h2 class="invite-title">Printed cards for the venue</h2>
            <!-- Catalog: invite.printed.subtitle.upload / invite.printed.subtitle.view -->
            <p class="is-sub">{{ access === 'view'
              ? 'Put these on tables. Guests scan to see the photos.'
              : 'Put these on tables. Guests scan to add their photos.' }}</p>
          </div>
        </div>
      </ng-template>
      <button type="button" class="invite-close" *ngIf="isDialog" (click)="closeOrBack()"
        [attr.aria-label]="screen === 'printed' ? 'Back to sharing' : 'Close'">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
          <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- ============ SCREEN 1 — the card, its look, and sharing ============ -->
    <!-- [hidden], not *ngIf: both cards stay in the DOM so either can be
         rasterised whichever screen is showing (preview upload, Download). -->
    <div class="is-invite" [hidden]="screen !== 'invite'">
      <div class="is-hero" (pointerdown)="onCardPointerDown($event)" (pointerup)="onCardPointerUp($event)">
        <div class="is-cardbox" role="button" tabindex="0" aria-label="See the card full size"
          (click)="openZoom()" (keydown.enter)="openZoom()">
          <!-- The link preview is rasterised from this wrapper (see savePreviewCard). -->
          <div #previewCardEl>
            <ng-container *ngTemplateOutlet="cardTpl; context: { $implicit: cardTemplate }"></ng-container>
          </div>
        </div>
        <!-- Straddles the bottom-right corner so it never covers template text. -->
        <button type="button" class="is-edit" *ngIf="canEditCover" [disabled]="coverSaving"
          (click)="$event.stopPropagation(); openCoverPicker()" aria-label="Change cover photo" title="Change cover photo">
          <span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M4 20h4L18.5 9.5a2.1 2.1 0 00-3-3L5 17v3z" stroke-linejoin="round" />
              <path d="M13.5 8.5l3 3" stroke-linecap="round" />
            </svg>
          </span>
        </button>
        <button type="button" class="is-nav is-nav--prev" (click)="stepTemplate(-1)" aria-label="Previous look">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M15 6l-6 6 6 6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
        <button type="button" class="is-nav is-nav--next" (click)="stepTemplate(1)" aria-label="Next look">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
      </div>

      <div class="is-picker">
        <p class="is-pick-label">Choose a look</p>
        <div class="is-picks" role="group" aria-label="Card look">
          <button type="button" class="is-pick" *ngFor="let t of templates"
            [attr.aria-pressed]="t.id === selectedTemplateId" (click)="selectTemplate(t.id)">
            <span class="is-thumb">
              <ng-container *ngTemplateOutlet="cardTpl; context: { $implicit: t }"></ng-container>
            </span>
            <span class="is-pick-name">{{ t.label }}</span>
          </button>
        </div>
      </div>

      <!-- Which link everything below hands out. The chosen side is a white
           pill on the grey track, never red: the red stays the share button's.
           Opens on "Add & see photos" every time (see `access`).
           Catalog: invite.access.label / .upload / .view. -->
      <div class="is-access" *ngIf="viewerInviteEnabled && !viewerUnavailable">
        <p class="is-pick-label">Guests can</p>
        <div class="is-seg" role="group" aria-label="What guests can do with the link">
          <button type="button" class="is-seg-opt" [attr.aria-pressed]="access === 'upload'" (click)="setAccess('upload')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="4" /><path d="M12 8v8M8 12h8" stroke-linecap="round" /></svg>
            Add &amp; see photos
          </button>
          <button type="button" class="is-seg-opt" [attr.aria-pressed]="access === 'view'" (click)="setAccess('view')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" stroke-linejoin="round" /><circle cx="12" cy="12" r="3" /></svg>
            See photos only
          </button>
        </div>
      </div>

      <div class="is-actions">
        <!-- One red button on its own line, two grey alternatives under it.
             Which button is red depends on how the host actually shares:
             - Phone in India: "Send on WhatsApp". Indian hosts post invites in
               WhatsApp groups; this opens WhatsApp with the message and link
               ready (the card shows as the link preview).
             - Phone elsewhere: "Share invite" (the OS share sheet), with
               WhatsApp as the first alternative.
             - Computer: "Copy link"; WhatsApp opens WhatsApp Web with the message.
             Every WhatsApp button opens WhatsApp directly, so it never feels
             the same as the share-sheet buttons beside it.
             The collect-by-WhatsApp route has no button of its own: every invite
             message already carries it, and the small print says so. -->
        <ng-container *ngIf="shareFirst; else copyFirst">
          <button type="button" class="is-btn is-btn--hero" *ngIf="indiaHost; else shareHero"
            (click)="linkFailed ? retryLink() : sendOnWhatsApp()" [disabled]="!inviteLink && !linkFailed">
            <ng-container *ngTemplateOutlet="waIcon"></ng-container>
            <!-- Catalog: invite.share.whatsapp -->
            {{ inviteLink ? 'Send on WhatsApp' : linkFailed ? "Couldn't get your link. Try again" : 'Preparing your link…' }}
          </button>
          <ng-template #shareHero>
            <button type="button" class="is-btn is-btn--hero"
              (click)="linkFailed ? retryLink() : shareVia('more')" [disabled]="!inviteLink && !linkFailed">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3v12M7 8l5-5 5 5M5 14v4a2 2 0 002 2h10a2 2 0 002-2v-4" stroke-linecap="round" stroke-linejoin="round" /></svg>
              {{ inviteLink ? 'Share invite' : linkFailed ? "Couldn't get your link. Try again" : 'Preparing your link…' }}
            </button>
          </ng-template>
          <div class="is-row" *ngIf="inviteLink">
            <button type="button" class="is-btn is-btn--sec" *ngIf="indiaHost; else waAlt" (click)="shareVia('more')">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3v12M7 8l5-5 5 5M5 14v4a2 2 0 002 2h10a2 2 0 002-2v-4" stroke-linecap="round" stroke-linejoin="round" /></svg>
              <!-- Catalog: invite.share.more -->More ways
            </button>
            <ng-template #waAlt>
              <button type="button" class="is-btn is-btn--sec" (click)="sendOnWhatsApp()">
                <ng-container *ngTemplateOutlet="waIcon"></ng-container>WhatsApp
              </button>
            </ng-template>
            <button type="button" class="is-btn is-btn--sec" (click)="goToPrintedCards()">
              <ng-container *ngTemplateOutlet="qrIcon"></ng-container>QR card
            </button>
          </div>
        </ng-container>
        <ng-template #copyFirst>
          <button type="button" class="is-btn is-btn--hero"
            (click)="linkFailed ? retryLink() : copyLink('copy_hero')" [disabled]="!inviteLink && !linkFailed">
            <ng-container *ngTemplateOutlet="linkIcon"></ng-container>
            {{ inviteLink ? (copied ? 'Link copied' : 'Copy link') : linkFailed ? "Couldn't get your link. Try again" : 'Preparing your link…' }}
          </button>
          <div class="is-row" *ngIf="inviteLink">
            <button type="button" class="is-btn is-btn--sec" (click)="sendOnWhatsApp()">
              <ng-container *ngTemplateOutlet="waIcon"></ng-container>WhatsApp
            </button>
            <button type="button" class="is-btn is-btn--sec" (click)="goToPrintedCards()">
              <ng-container *ngTemplateOutlet="qrIcon"></ng-container>QR card
            </button>
          </div>
        </ng-template>
        <!-- Catalog: invite.share.fineprint. The WhatsApp mark makes the one
             mention of the collect-by-WhatsApp route get read. Only named when
             the route is really in the message (offersWhatsapp); the view-only
             link offers no collecting at all.
             Catalog: invite.share.fine.upload / invite.share.fine.view -->
        <p class="is-fine" *ngIf="access === 'upload' && whatsappUploadUrl; else plainFine">Anyone with the link can add and see photos, or send them in a
          <span class="is-fine-wa"><ng-container *ngTemplateOutlet="waIcon"></ng-container>WhatsApp</span> chat.</p>
        <ng-template #plainFine>
          <p class="is-fine">{{ access === 'view'
            ? "Anyone with this link can see the photos, but can't add any."
            : 'Anyone with the link can add photos and see the event.' }}</p>
        </ng-template>
        <button type="button" class="plain-qr-link" [disabled]="!inviteQr" (click)="downloadQr()">
          Download plain QR
        </button>
      </div>
    </div>

    <!-- ============ SCREEN 2 — printed cards for the venue ============ -->
    <div class="is-printed" [hidden]="screen !== 'printed'">
      <!-- The chosen look on top, the QR panel below, A6-shaped. Download and
           Share rasterise exactly this (see prepareCard). -->
      <div #printCardEl class="ic-print ic-unit" appCardUnit
        [style.--pc-bg]="cardTemplate.panelBackground" [style.--pc-fg]="cardTemplate.panelColor">
        <ng-container *ngTemplateOutlet="cardTpl; context: { $implicit: cardTemplate }"></ng-container>
        <div class="ic-panel">
          <div class="ic-qr">
            <img *ngIf="inviteQr" [src]="inviteQr" alt="QR code for this event" />
            <span class="ic-qr-loading" *ngIf="!inviteQr && qrLoading">Preparing your code…</span>
          </div>
          <div class="ic-panel-text">
            <!-- Catalog: invite.card.scan.upload / invite.card.scan.view -->
            <b>{{ access === 'view' ? 'Scan to see the photos' : 'Scan to add your photos' }}</b>
            <span>No app. No account. Just your camera.</span>
          </div>
        </div>
      </div>

      <!-- Which of these leads depends on the device, for the same reason the
           invite row's hero does (see shareFirst). On a phone, Share sends the
           card into a chat with the link beside it, and the host is holding
           the thing they want to send from. On desktop there is no file share
           to reach — shareCard would quietly degrade to copying a link — and
           this screen is printed cards for a venue, which is a job you do at a
           printer. So the desktop lead is Download.

           --lead only on the phone. Desktop gives this row the full dialog
           width, where "widest slot" turns into a 90% button beside a 69px
           pill; two equal halves and the colour difference carry the hierarchy
           well enough at that size. -->
      <div class="is-row" [class.is-row--lead]="shareFirst" [class.is-row--two]="!shareFirst">
        <ng-container *ngIf="shareFirst; else downloadFirst">
          <button type="button" class="is-btn is-btn--main is-btn--lead" (click)="shareCard()" [disabled]="!inviteQr">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3v12M7 8l5-5 5 5M5 14v4a2 2 0 002 2h10a2 2 0 002-2v-4" stroke-linecap="round" stroke-linejoin="round" /></svg>
            Share card
          </button>
          <button type="button" class="is-btn is-btn--sec" (click)="saveCard()" [disabled]="!inviteQr">
            <ng-container *ngTemplateOutlet="downloadIcon"></ng-container>Download
          </button>
        </ng-container>
        <ng-template #downloadFirst>
          <button type="button" class="is-btn is-btn--main is-btn--lead" (click)="saveCard()" [disabled]="!inviteQr">
            <ng-container *ngTemplateOutlet="downloadIcon"></ng-container>Download card
          </button>
          <button type="button" class="is-btn is-btn--sec" (click)="shareCard()" [disabled]="!inviteQr">Share</button>
        </ng-template>
      </div>
      <p class="is-fine">Uses the look you picked. Prints well at A6 (105 × 148 mm) or larger.</p>

      <ng-container *ngIf="cardDesignerEnabled">
        <div class="printed-divider"></div>
        <div class="section-label section-label--muted">Or make your own card</div>

        <button type="button" class="printed-row" (click)="openCardDesigner()">
          <span class="venue-row-icon venue-row-icon--design">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span class="venue-row-text">
            <span class="venue-row-title">Design your own card</span>
            <span class="venue-row-sub">Build a custom layout — your QR code, your style</span>
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="venue-row-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <div class="section-label section-label--muted section-label--spaced">Or pick a Canva template</div>

        <div class="canva-scroll">
          <button type="button" class="canva-tile" *ngFor="let t of cardTemplates" (click)="openCanvaTemplate(t)">
            <img [src]="t.previewImage" [alt]="t.title" onerror="this.src='assets/images/default_cover.png'" />
            <span class="canva-tile-title">{{ t.title }}</span>
          </button>
        </div>

        <div class="printed-see-all">
          <button type="button" class="printed-see-all-btn" (click)="goBrowseTemplates()">See all templates →</button>
        </div>

        <p class="field-hint printed-fine">Opens in Canva — add the QR code above, then customize and print.</p>
      </ng-container>
    </div>

    <!-- Tap the card: see it full size. -->
    <div class="is-zoom" *ngIf="zoomOpen" (click)="closeZoom()" role="dialog" aria-label="The invite card, full size">
      <div class="is-zoom-card">
        <ng-container *ngTemplateOutlet="cardTpl; context: { $implicit: cardTemplate }"></ng-container>
      </div>
      <span class="is-zoom-hint">Tap anywhere to close</span>
    </div>

    <!-- ── The card itself, one markup per template (invite-card-templates.ts).
         Every size is calc(var(--u) * n), --u being 1% of the card's width
         (CardUnitDirective), so the same markup draws the big card, the
         thumbnails, the zoom and the exported images. ── -->
    <ng-template #cardTpl let-t>
      <div class="ic-card ic-unit ic-{{ t.id }}" appCardUnit
        [style.--img]="cardCoverCss" [style.--nm]="nameUnits(t)">
        <ng-container [ngSwitch]="t.id">
          <ng-container *ngSwitchCase="'polaroid'">
            <div class="ic-ey">You're invited</div>
            <div class="ic-frame">
              <div class="ic-tape"></div>
              <div class="ic-ph"></div>
              <div class="ic-chin"><div class="ic-nm">{{ cardName }}</div></div>
            </div>
            <div class="ic-dt" *ngIf="cardDate">{{ cardDate }}</div>
            <div class="ic-mk">PICAGGO</div>
          </ng-container>
          <ng-container *ngSwitchCase="'editorial'">
            <div class="ic-ph"></div>
            <div class="ic-inset"></div>
            <div class="ic-mk">PICAGGO</div>
            <div class="ic-band">
              <div class="ic-ey">You're invited</div>
              <div class="ic-nm">{{ cardName }}</div>
              <div class="ic-dt" *ngIf="cardDate">{{ cardDate }}</div>
            </div>
          </ng-container>
          <ng-container *ngSwitchCase="'stack'">
            <div class="ic-pile">
              <div class="ic-pr ic-pr--b1"><div class="ic-ph"></div></div>
              <div class="ic-pr ic-pr--b2"><div class="ic-ph"></div></div>
              <div class="ic-pr ic-pr--f"><div class="ic-ph"></div></div>
            </div>
            <div class="ic-mk">PICAGGO</div>
            <div class="ic-tx">
              <div class="ic-nm">{{ cardName }}</div>
              <div class="ic-dt" *ngIf="cardDate"><i></i>{{ cardDate }}</div>
            </div>
          </ng-container>
          <ng-container *ngSwitchDefault>
            <div class="ic-ph"></div>
            <div class="ic-shade"></div>
            <div class="ic-mk">PICAGGO</div>
            <!-- The eyebrow rides with the name inside the bottom fade: a cover with
                 its own text at the top would swallow it up there. -->
            <div class="ic-tx">
              <div class="ic-ey">You're invited</div>
              <div class="ic-nm">{{ cardName }}</div>
              <div class="ic-dt" *ngIf="cardDate">{{ cardDate }}</div>
            </div>
          </ng-container>
        </ng-container>
      </div>
    </ng-template>

    <ng-template #waIcon>
      <svg class="is-wa" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" /></svg>
    </ng-template>
    <ng-template #linkIcon>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M10 14a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66l-1 1M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l1-1" stroke-linecap="round" /></svg>
    </ng-template>
    <ng-template #downloadIcon>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 4v11M7 10l5 5 5-5M5 19h14" stroke-linecap="round" stroke-linejoin="round" /></svg>
    </ng-template>
    <ng-template #qrIcon>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1.2" /><rect x="14" y="3" width="7" height="7" rx="1.2" /><rect x="3" y="14" width="7" height="7" rx="1.2" /><path d="M14 14h3v3h-3zM19 14h2v2h-2zM14 19h2v2h-2zM19 19h2v2h-2z" fill="currentColor" stroke="none" /></svg>
    </ng-template>
  </ng-container>

  <!-- ============================================================
       LEGACY BODY — unchanged, kept for environment.features.newInviteDesign
       being off. Delete once the new design has shipped for real.
       ============================================================ -->
  <ng-template #legacyBody>

    <!-- Dialog mode brings its own header; inline, the sheet already has one. -->
    <div class="invite-head" *ngIf="isDialog">
      <h2 class="invite-title">Send it to friends before you forget</h2>
      <button type="button" class="invite-close" (click)="done()" aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
          <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- Screen 1 owns the hero copy inline only — the dialog surface already has
         its own header (above) making the same point, and showing both would say
         the same thing twice. -->
    <div class="invite-hero" *ngIf="!isDialog && screen === 'invite'">
      <div class="created-tag">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <path d="M4 12l5 5L20 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>Event created</span>
      </div>
      <div class="hero-name">{{ eventName }}</div>
      <div class="hero-headline">Send the link — their photos land here</div>
      <div class="hero-subcopy">No app, no account. Guests just tap and add.</div>
    </div>

    <!-- ============ SCREEN 1 — share the link ============ -->
    <!-- The URL and the named destinations are one decision for the host — "how do
         I get this to people" — so they live in one section. The link is in the
         open rather than hidden behind a button: a host who wants to paste it
         somewhere we don't offer can read it and take it. -->
    <div class="share-section" *ngIf="screen === 'invite'">
      <div class="section-label">Share online</div>

      <div class="share-panel">
        <div class="invite-url">
          <div class="url-row">
            <ng-container *ngIf="inviteLink; else urlLoading">
              <input id="inviteUrl" class="url-input" type="text" readonly [value]="inviteLink"
                (focus)="selectUrl($event)" aria-label="Invite link" />
              <button type="button" class="url-copy" (click)="copyLink('url_field')">
                {{ copied ? 'Copied' : 'Copy' }}
              </button>
            </ng-container>
            <ng-template #urlLoading>
              <span class="url-loading" role="status">
                <span class="url-spinner" aria-hidden="true"></span>
                Preparing your link…
              </span>
            </ng-template>
          </div>
        </div>

      <!-- Named destinations rather than one "Share" button. The OS sheet is
           still there under "More", but it costs an extra tap and a decision,
           and this step is the one place the whole album depends on. -->
      <div class="share-targets">
        <button type="button" class="share-target" (click)="shareVia('whatsapp')" [disabled]="!inviteLink">
          <span class="share-icon share-icon--whatsapp">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
            </svg>
          </span>
          <span class="share-label">WhatsApp</span>
        </button>

        <button type="button" class="share-target" (click)="shareVia('sms')" [disabled]="!inviteLink">
          <span class="share-icon share-icon--message">
            <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 3C6.9 3 2.8 6.5 2.8 10.9c0 2.5 1.35 4.72 3.45 6.18v3.3a.5.5 0 0 0 .75.43l3.2-1.85c.58.09 1.18.14 1.8.14 5.1 0 9.2-3.5 9.2-7.9S17.1 3 12 3Z" />
            </svg>
          </span>
          <span class="share-label">Message</span>
        </button>

        <button type="button" class="share-target" (click)="shareVia('email')" [disabled]="!inviteLink">
          <span class="share-icon share-icon--email">
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
              <rect x="2.8" y="5.2" width="18.4" height="13.6" rx="2.4" />
              <path d="M3.6 7.4l7.28 4.98a2 2 0 0 0 2.24 0L20.4 7.4" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </span>
          <span class="share-label">Email</span>
        </button>

        <button type="button" class="share-target" (click)="shareVia('more')" [disabled]="!inviteLink">
          <span class="share-icon share-icon--more">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="6" cy="12" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="18" cy="12" r="1.9" />
            </svg>
          </span>
          <span class="share-label">More</span>
        </button>
      </div>
      </div>

      <p class="field-hint">Anyone with this link can add photos and see all of them.</p>

      <!-- "For the venue" groups the two things a host does *at* the event rather
           than sends ahead of it — kept visually apart from the link/share panel
           above so the three mechanisms (send a link, auto-collect via chat,
           put up a physical card) read as different decisions, not one list. -->
      <div class="venue-group">
        <div class="section-label section-label--muted">For the venue</div>

        <!-- A second, separate route for the guests who will never open the link
             at all — they just send photos into a WhatsApp chat. Kept apart from
             the share row above (and its own WhatsApp icon) so the two don't
             read as the same action behind two identical icons. Appears only
             once the code is minted, so it never shows an empty or half-ready
             row; the printed-cards row below it doesn't depend on this feature,
             so it stays outside this condition. -->
        <div class="venue-row" *ngIf="whatsappUploadUrl">
          <span class="venue-row-icon venue-row-icon--whatsapp">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
            </svg>
          </span>
          <span class="venue-row-text">
            <span class="venue-row-title">Collect photos via WhatsApp</span>
            <span class="venue-row-sub">Guests send photos in the chat, they land here<ng-container *ngIf="whatsappUploadValidityLabel"> · Valid until {{ whatsappUploadValidityLabel }}</ng-container></span>
          </span>
          <button type="button" class="venue-row-copy" (click)="copyWhatsappUpload()">
            {{ whatsappCopied ? 'Copied' : 'Copy' }}
          </button>
        </div>

        <!-- Printed cards — a physical QR the venue puts on a table. Behind a row
             rather than expanded inline: the card, the designer and the Canva
             gallery are a whole extra decision most hosts won't make today, and
             this keeps that decision from crowding out the link every host does
             need right now. -->
        <button type="button" class="venue-row venue-row--tap" (click)="goToPrintedCards()">
          <span class="venue-row-icon venue-row-icon--qr">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="3" y="3" width="7" height="7" rx="1.2" />
              <rect x="14" y="3" width="7" height="7" rx="1.2" />
              <rect x="3" y="14" width="7" height="7" rx="1.2" />
              <path d="M14 14h3v3h-3zM19 14h2v2h-2zM14 19h2v2h-2zM19 19h2v2h-2z" fill="currentColor" stroke="none" />
            </svg>
          </span>
          <span class="venue-row-text">
            <span class="venue-row-title">Printed cards for the venue</span>
            <span class="venue-row-sub">A QR card guests can scan on the table</span>
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="venue-row-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- ============ SCREEN 2 — printed cards ============ -->
    <div class="printed-section" *ngIf="screen === 'printed'">
      <div class="printed-head">
        <button type="button" class="printed-back" (click)="goBackToInvite()" aria-label="Back to sharing">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <path d="M15 6l-6 6 6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="printed-title">Printed cards for the venue</div>
      </div>

      <!-- Same markup `saveCard()`/`shareCard()` rasterise — this is now the
           only place the card renders, so it doubles as its own preview. -->
      <div class="invite-card" #inviteCardEl>
        <div class="invite-eyebrow">You're invited to share</div>
        <div class="invite-name">{{ eventName }}</div>
        <div class="invite-when">{{ cardDate }}</div>

        <div class="invite-qr">
          <img *ngIf="inviteQr" [src]="inviteQr" alt="QR code for this event" />
          <div class="invite-qr-loading" *ngIf="!inviteQr && qrLoading">Preparing your code…</div>
        </div>

        <div class="invite-scan">Scan to add your photos</div>
        <div class="invite-fine">No app. No account. Just your camera.</div>
      </div>

      <div class="printed-actions">
        <button type="button" class="printed-download" (click)="saveCard()" [disabled]="!inviteQr">Download card</button>
        <button type="button" class="printed-share" (click)="shareCard()" [disabled]="!inviteQr">Share</button>
      </div>

      <ng-container *ngIf="cardDesignerEnabled">
        <div class="printed-divider"></div>
        <div class="section-label section-label--muted">Or make your own card</div>

        <button type="button" class="printed-row" (click)="openCardDesigner()">
          <span class="venue-row-icon venue-row-icon--design">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span class="venue-row-text">
            <span class="venue-row-title">Design your own card</span>
            <span class="venue-row-sub">Build a custom layout — your QR code, your style</span>
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="venue-row-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <div class="section-label section-label--muted section-label--spaced">Or pick a Canva template</div>

        <div class="canva-scroll">
          <button type="button" class="canva-tile" *ngFor="let t of cardTemplates" (click)="openCanvaTemplate(t)">
            <img [src]="t.previewImage" [alt]="t.title" onerror="this.src='assets/images/default_cover.png'" />
            <span class="canva-tile-title">{{ t.title }}</span>
          </button>
        </div>

        <div class="printed-see-all">
          <button type="button" class="printed-see-all-btn" (click)="goBrowseTemplates()">See all templates →</button>
        </div>

        <p class="field-hint printed-fine">Opens in Canva — add the QR code above, then customize and print.</p>
      </ng-container>
    </div>

    <!-- Dialog mode needs a way out that isn't the backdrop; inline, the sheet's
         own footer moves the host on to step 5. Only on screen 1 — screen 2's
         back arrow is the way out of the printed-cards view. -->
    <button type="button" class="invite-done" *ngIf="isDialog && screen === 'invite'" (click)="done()">
      Go to my event
    </button>
  </ng-template>
</div>
